import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { ProjectDto, CreateProjectRequest, UpdateProjectRequest, CreateProjectTaskRequest, UpdateProjectTaskRequest, CreateProjectMessageRequest } from '../types';

const projectsKeys = {
  all: ['projects'] as const,
  lists: () => [...projectsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...projectsKeys.lists(), filters] as const,
  details: () => [...projectsKeys.all, 'detail'] as const,
  detail: (id: number) => [...projectsKeys.details(), id] as const,
};

// Get all projects with optional filters
export const useProjects = (filters?: {
  brgId?: number;
  status?: string;
  company?: string;
  priority?: string;
}) => {
  return useQuery({
    queryKey: projectsKeys.list(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.brgId) params.append('brgId', filters.brgId.toString());
      if (filters?.status) params.append('status', filters.status);
      if (filters?.company) params.append('company', filters.company);
      if (filters?.priority) params.append('priority', filters.priority);

      const response = await apiClient.get<ProjectDto[]>(`/api/projects?${params.toString()}`);
      return response;
    },
  });
};

// Get single project
export const useProject = (id: number) => {
  return useQuery({
    queryKey: projectsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<ProjectDto>(`/api/projects/${id}`);
      return response;
    },
    enabled: !!id,
  });
};

// Create project
export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectRequest) =>
      apiClient.post<number>('/api/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Update project
export const useUpdateProject = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectRequest) =>
      apiClient.put<number>(`/api/projects/${projectId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Delete project
export const useDeleteProject = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete(`/api/projects/${projectId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Create task
export const useCreateTask = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectTaskRequest) =>
      apiClient.post<number>(`/api/projects/${projectId}/tasks`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Update task
export const useUpdateTask = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { taskId: number; request: UpdateProjectTaskRequest }) =>
      apiClient.put<number>(`/api/projects/tasks/${data.taskId}`, data.request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Delete task
export const useDeleteTask = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: number) =>
      apiClient.delete(`/api/projects/tasks/${taskId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};

// Create message
export const useCreateMessage = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<CreateProjectMessageRequest, 'projectId'>) =>
      apiClient.post<number>(`/api/projects/${projectId}/messages`, {
        ...data,
        projectId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectsKeys.detail(projectId) });
      queryClient.invalidateQueries({ queryKey: projectsKeys.lists() });
    },
  });
};
